import { getAnalytics } from "@tm/insurance/analytics";
import {
  getSelectedCountriesFromLocalStorage,
  initializePageTracking,
} from "@tm/insurance/analytics/xdm";
import { pageStack, PAGE_NAME_CONSTANTS } from "@tm/insurance/navigation";
import {
  convertDateToUnix,
  convertUnixToDate,
  findTravellerIndexByCheckoutId,
  getFormFieldErrorMsg,
  reorderTravellersBySelectionOrder,
} from "@tm/insurance/utils";
import { constants } from "@tm/isomorphic-utils";
import { BreadcrumbComponent } from "@tm/static";
import { usePlanType } from "@tm/ui";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { ClientError } from "@tm/utility";
import { trackEvent } from "libs/utility/src/lib/analytics/XDMAnalytics/xdmEvents";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";
import { uploadTemplate } from "../../../components/ExcelUpload/utils/uploadTemplate";
import { MYPARTNER_COMPONENT_MAPPER } from "../../../components/mypartner";
import WebToast from "../../../components/Toast/WebToast";
import {
  HeadComp,
  LeftComp,
  MainContainer,
  RightComp,
  TravellerDetailLayoutV2,
  WebToastWrap,
} from "./InsuranceTravelDetailsV2.styles";
export interface IPrimaryContact {
  email: string;
  mobile: string;
}
export interface ISecondaryContactV2 {
  name: string;
  dob: string;
  gender: string;
  isSelected: boolean;
  isEdited?: boolean;
  isSavedTraveller?: boolean;
  isExcelUploaded?: boolean;
  passport?: string;
  nomineeName?: string;
  selectionOrder?: number;
  travellerId?: number | string;
  infoSource?: string;
}

export interface ITravellerDetailsFormV2 {
  travellerData: ISecondaryContactV2[];
  primaryContactData: IPrimaryContact;
  tncs: boolean[];
}

interface TravellerError {
  index: number;
  errorType: string;
}

export interface ISecondaryContactFields {
  name?: { isRequired?: boolean };
  dob?: { isRequired?: boolean };
  gender?: { isRequired?: boolean };
  passport?: { isRequired?: boolean };
  nomineeName?: { isRequired?: boolean };
}

export type InsTravDetailV2Props = {
  travellerDetailData: {
    components: {
      id: string;
      data: any;
    }[];
    pageData: any;
  };
  // Error handling props (same as InsTravDetailProps)
  setActiveModal: (modal: string) => void;
  isLoading: boolean;
  checkoutError?: any;
  handleTravellerPayment: (formData: any) => Promise<void>;
  onFormDataChange?: (formData: any) => void;
  getSelectedCountry?: (selectedCountry: any) => void;
  firstErrorIndex: number;
  setFirstErrorIndex: (index: number) => void;
  onSetError?: any;
  serverErrors?: Array<{
    travellerId: string;
    displayError: {
      name?: string;
      dob?: string;
      gender?: string;
      passport?: string;
      nomineeName?: string;
    };
  }>;
  // MyPartner specific props
  selectedCountry?: { countryCode: string };
  travellerCount?: number;
};

const InsTravDetailV2 = ({
  travellerDetailData: InsTravDetailV2,
  handleTravellerPayment,
  onFormDataChange,
  selectedCountry,
  getSelectedCountry,
  setActiveModal,
  isLoading: externalIsLoading,
  checkoutError,
  firstErrorIndex,
  setFirstErrorIndex,
  onSetError,
  serverErrors,
}: InsTravDetailV2Props) => {
  const [secondaryContactFields, setSecondaryContactFields] =
    useState<ISecondaryContactFields>({});
  const { components, pageData } = InsTravDetailV2;
  const { travellerCount, errorToasts } = pageData || {};
  const {
    formErrorToast: pageFormErrorToast,
    addErrorToast: pageAddErrorToast,
  } = errorToasts || {};
  const { formErrorToast, addErrorToast } = constants;

  // Analytics and plan type
  const analytics = getAnalytics();

  useEffect(() => {
    pageStack.push(PAGE_NAME_CONSTANTS.TRAVELLER_DETAILS);
    analytics?.trackPageLoadEvent({
      pageName: "REVIEW",
      experiment: pageData?.experiment,
    });
    const queryParams = new URLSearchParams(window.location.search);
    const selectedCountries = getSelectedCountriesFromLocalStorage(
      queryParams.get("countries")?.split(",") || []
    );

    const segment = queryParams.get("segment");
    const startDate = queryParams.get("startDate");
    const endDate = queryParams.get("endDate");
    let start, end;
    if (startDate && endDate) {
      start = convertUnixToDate(Number(startDate));
      end = convertUnixToDate(Number(endDate));
    }
    const travellers = queryParams.get("travellers");
    const subpageName = queryParams.get("type");
    const productId = queryParams.get("productId");
    initializePageTracking(
      {
        page_context: {
          page_name: "review",
          page_type: "funnel",
          funnel_step: "review",
          ...(subpageName && { subpage_name: `${subpageName}_mypartner` }),
        },
        event_detail: {
          ...(productId && {
            components: [
              {
                product_list: [{ id: productId ?? "" }],
              },
            ],
          }),
        },
      },
      {
        countries: selectedCountries,
        journeyType: segment || "",
        travellers: travellers ? parseInt(travellers) : 1,
        fromDateTime: start ? convertDateToUnix(start) : 0,
        toDateTime: end ? convertDateToUnix(end) : 0,
        isStoreSearchContext: true,
      }
    );
  }, []);
  const { type } = usePlanType();
  const device = useBreakpointV2();

  // Use external loading state if provided, otherwise use local state
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const isLoading =
    externalIsLoading !== undefined ? externalIsLoading : localIsLoading;

  // Validation state
  const [isSelectedError, setIsSelectedError] = useState<boolean>(false);
  const [isFieldError, setIsFieldError] = useState<boolean>(false);

  const methods = useForm<ITravellerDetailsFormV2>({
    defaultValues: {
      travellerData: Array.from({
        length: pageData?.travCount ?? 2, // TODO: get length of travCount and savedTravellers length
      }).map(() => ({
        name: "",
        dob: "",
        gender: "",
        passport: "",
        nomineeName: "",
        isSelected: false,
      })),
      primaryContactData: {
        email: "",
        mobile: "",
      },
      tncs: Array.from({ length: 1 }).map(() => true), // TODO: get length of tncs
    },
    mode: "onTouched",
  });

  const { control, getValues, setValue, setError, handleSubmit, trigger } =
    methods;

  const formValues = getValues();

  // Watch traveller data to track selected count
  const travellerData = useWatch({
    control,
    name: "travellerData",
  });

  // Calculate selected count
  const selectedCount = useMemo(
    () =>
      travellerData?.filter((traveller) => traveller.isSelected)?.length ?? 0,
    [travellerData]
  );

  // References for scrolling to errors
  const travellerRef = useRef<HTMLInputElement>(null);
  const primaryContactRef = useRef<HTMLInputElement>(null);
  const tncRef = useRef<HTMLInputElement>(null);

  // State to control which traveller is expanded (for collapse/expand animation)
  const [expandedTravellerIndex, setExpandedTravellerIndex] = useState(-1);

  // State to store uploaded Excel file for triggering upload before payment
  const [uploadedExcelFile, setUploadedExcelFile] = useState<File | null>(null);

  // State to force showing all travellers when error is on non-visible traveller
  const [forceShowAllTravellers, setForceShowAllTravellers] = useState(false);

  // Reusable method to scroll to error traveller
  const scrollToErrorTraveller = (errorIndex: number, delay: number = 300) => {
    setTimeout(() => {
      // Try to find the specific traveller element by data attribute
      const errorTravellerElement = document.querySelector(
        `[data-traveller-index="${errorIndex}"]`
      );
      if (errorTravellerElement) {
        errorTravellerElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      } else {
        // Fallback to travellerRef
        travellerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, delay);
  };

  // Handle checkout errors - throw as ClientError like in AddTravellerDetails
  if (checkoutError && typeof window !== "undefined") {
    throw new ClientError(checkoutError);
  }

  // Handle firstErrorIndex - expand error traveller and scroll to it
  useEffect(() => {
    if (firstErrorIndex !== undefined && firstErrorIndex >= 0) {
      // Expand the error traveller
      setExpandedTravellerIndex(firstErrorIndex);

      // Scroll to error traveller after collapse animation
      scrollToErrorTraveller(firstErrorIndex, 300);
    }
    if (setFirstErrorIndex) {
      setFirstErrorIndex(-1);
    }
  }, [firstErrorIndex, setFirstErrorIndex]);

  // Handle onSetError - set form errors when onSetError prop changes
  useEffect(() => {
    if (onSetError) {
      setError(onSetError?.errTrype, onSetError?.errName);
    }
  }, [onSetError, setError]);
  // Handle server errors (checkout errors) with reordering
  useEffect(() => {
    if (!serverErrors || serverErrors.length === 0) return;

    // Find travellers with actual errors (non-empty error messages)
    const travellersWithErrors: {
      index: number;
      errors: { field: string; message: string }[];
    }[] = [];

    serverErrors.forEach((item) => {
      const { name, dob, gender, passport, nomineeName } =
        item.displayError || {};
      const errors: { field: string; message: string }[] = [];

      if (name) errors.push({ field: "name", message: name });
      if (dob) errors.push({ field: "dob", message: dob });
      if (gender) errors.push({ field: "gender", message: gender });
      if (passport) errors.push({ field: "passport", message: passport });
      if (nomineeName)
        errors.push({ field: "nomineeName", message: nomineeName });

      if (errors.length > 0) {
        const currentData = getValues("travellerData") || [];
        const index = findTravellerIndexByCheckoutId(
          currentData,
          item.travellerId
        );
        if (index >= 0) {
          travellersWithErrors.push({
            index,
            errors,
          });
        }
      }
    });

    if (travellersWithErrors.length === 0) return;

    // Get current form data
    const currentData = getValues("travellerData");
    if (!currentData) return;

    // Create new order: error travellers first, then others
    const errorIndices = travellersWithErrors.map((t) => t.index);
    const newArray: any[] = [];

    // First add travellers with errors
    errorIndices.forEach((idx) => {
      if (currentData[idx]) {
        newArray.push(currentData[idx]);
      }
    });

    // Then add travellers without errors
    currentData.forEach((traveller: any, idx: number) => {
      if (!errorIndices.includes(idx)) {
        newArray.push(traveller);
      }
    });

    // Update form with reordered data
    methods.reset(
      {
        ...getValues(),
        travellerData: newArray,
      },
      { keepDefaultValues: true }
    );

    // Wait for state update then set errors on new indices
    setTimeout(() => {
      travellersWithErrors.forEach((trav, newIndex) => {
        trav.errors.forEach((err) => {
          setError(`travellerData.${newIndex}.${err.field}` as any, {
            message: err.message,
          });
        });
      });

      // Expand first traveller with error
      setExpandedTravellerIndex(0);

      // Show error toast
      if (!isFieldError) {
        setIsFieldError(true);
        handleErrorToast();
      }

      // Scroll to first error
      scrollToErrorTraveller(0, 350);
    }, 100);
  }, [serverErrors]);

  const handleErrorToast = () => {
    setTimeout(() => {
      setIsSelectedError(false);
      setIsFieldError(false);
    }, 3000);
  };

  const handleTncCheckbox = (index: number) => {
    const value = formValues.tncs[index];
    analytics?.trackTravDetailsEvents({
      actionType: `disclaimer_${value ? "unchecked" : "checked"}`,
    });
    setValue(`tncs.${index}`, !value);
    trigger(`tncs.${index}`);
  };

  type FormFieldType = "name" | "dob" | "gender" | "passport" | "nomineeName";

  const filterTravellers = async (
    selected: number[],
    errors: TravellerError[]
  ) => {
    // Get fresh form values (all travellers should be visible at this point)
    const currentData = getValues("travellerData") || [];

    let targetPosition = 0;
    const newIndices: any = new Array(currentData.length);
    const errorIndices = errors.map((err) => err.index);

    currentData.forEach((traveller: any, index: number) => {
      if (errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    currentData.forEach((traveller: any, index: number) => {
      if (selected.includes(index) && !errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    currentData.forEach((traveller: any, index: number) => {
      if (!selected.includes(index) && !errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    const newArray: any = [];
    newIndices.forEach((indexVal: number, index: number) => {
      newArray[indexVal] = currentData[index];
    });

    // Use reset to force form state update and trigger re-render in all child components
    const currentFormValues = getValues();
    methods.reset(
      {
        ...currentFormValues,
        travellerData: newArray,
      },
      { keepDefaultValues: true }
    );

    // Wait for React to process the state update before triggering validation
    await new Promise((resolve) => setTimeout(resolve, 50));
    await trigger("travellerData");

    // If there are errors, show all travellers to make error travellers visible
    if (errors.length > 0) {
      setForceShowAllTravellers(true);
    }

    errors.forEach(({ index, errorType }, ind) => {
      const fieldKey = `travellerData.${ind}.${errorType}` as
        | `travellerData.${number}.name`
        | `travellerData.${number}.dob`
        | `travellerData.${number}.gender`
        | `travellerData.${number}.passport`
        | `travellerData.${number}.nomineeName`;

      // errorTrackEvent({
      //   error_details_list: [{
      //     error_message: getFormFieldErrorMsg(errorType as FormFieldType),
      //     error_source: fieldKey,
      //     error_severity: 3,
      //   }],
      // })

      setError(fieldKey, {
        message: getFormFieldErrorMsg(errorType as FormFieldType),
      });
    });
  };

  // Simplified payment flow without login logic
  const onSubmit: SubmitHandler<ITravellerDetailsFormV2> = async (formData) => {
    // Only set local loading state if no external loading management
    if (externalIsLoading === undefined) {
      setLocalIsLoading(true);
    }
    try {
      // Pass form data to parent component for handling
      if (onFormDataChange) {
        onFormDataChange(formData);
      }

      // Pass selected country to parent
      if (getSelectedCountry && selectedCountry) {
        getSelectedCountry(selectedCountry);
      }

      // Track analytics events
      analytics?.trackTravDetailsEvents(
        { actionType: "traveller_name_filled" },
        "pageView",
        type
      );
      analytics?.trackTravDetailsEvents(
        { actionType: "contact_details_filled" },
        "pageView",
        type
      );
      analytics?.trackTravDetailsEvents(
        { actionType: "dob_selected" },
        "pageView",
        type
      );
      analytics?.trackTravDetailsEvents(
        { actionType: "gender_selected" },
        "pageView",
        type
      );
      // Trigger Excel upload if excelSectionData exists and isExcelBypass is false
      const excelSectionData = components?.find(
        (c: any) => c?.data?.excelSectionData
      )?.data?.excelSectionData;
      const isExcelBypass = excelSectionData?.upload?.isExcelBypass;

      if (excelSectionData && !isExcelBypass && uploadedExcelFile) {
        const queryParams = new URLSearchParams(window.location.search);
        const quoteId = queryParams.get("quoteId") || "";
        const itineraryId = queryParams.get("itineraryId") || "";
        // Fire and forget - don't await the response
        uploadTemplate(uploadedExcelFile, quoteId, itineraryId, false).catch(
          (err) => console.error("Excel upload failed:", err)
        );
      }

      // Since user is always logged in, proceed directly to payment
      if (handleTravellerPayment) {
        await handleTravellerPayment(formData);
      }
    } catch (error) {
      console.error("Payment processing error:", error);
    } finally {
      // Only manage local loading state if no external loading management
      if (externalIsLoading === undefined) {
        setLocalIsLoading(false);
      }
    }
  };

  // Handle form validation errors
  const onSubmitError: SubmitErrorHandler<ITravellerDetailsFormV2> = (
    errors
  ) => {
    // Only manage local loading state if no external loading management
    if (externalIsLoading === undefined) {
      setLocalIsLoading(false);
    }

    if (!isFieldError) {
      setIsFieldError(true);
      handleErrorToast();
    }

    // Handle setting firstErrorIndex like in AddTravellerDetails with improved animation
    if (errors.travellerData?.length) {
      const errorIndex = errors.travellerData?.findIndex?.((error) => error);
      if (errorIndex != null && errorIndex >= 0 && setFirstErrorIndex) {
        // Expand first traveller with error
        setExpandedTravellerIndex(errorIndex);
        setFirstErrorIndex(errorIndex);

        // Scroll to error traveller after collapse animation
        scrollToErrorTraveller(errorIndex, 350);
      }
    } else if (
      errors.primaryContactData?.email ||
      errors.primaryContactData?.mobile
    ) {
      // Add animation delay for primary contact errors
      setTimeout(() => {
        primaryContactRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "center",
        });
      }, 150);
    } else if (errors.tncs?.length) {
      // Add animation delay for TnC errors
      setTimeout(() => {
        tncRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "center",
        });
      }, 150);
    }
  };

  // Main validation and buy handler like in AddTravellerDetails
  const handleBuy = async (fromBenefitModal: boolean = false) => {
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "buy_clicked",
      },
    });
    analytics?.trackTravDetailsEvents(
      { actionType: "buy_clicked" },
      "pageView",
      type
    );

    if (selectedCount !== travellerCount) {
      if (!isFieldError) {
        setIsFieldError(true);
        // Keep collapsed - no specific field error, just need to select more travellers
        setExpandedTravellerIndex(-1);
        // Scroll to first traveller
        scrollToErrorTraveller(0, 300);
        handleErrorToast();
      }
    } else {
      // Show all travellers before validation so all form fields are rendered
      setForceShowAllTravellers(true);

      // Wait for UI to update and form fields to register
      await new Promise((resolve) => setTimeout(resolve, 100));

      const selected: number[] = [];
      const errorArr: TravellerError[] = [];

      // Get fresh form values after showing all travellers
      const formValues = getValues();

      formValues?.travellerData?.map((item, index) => {
        const {
          name: fullName,
          dob: dobValue,
          gender: genderType,
          passport: passportType,
          nomineeName: nomineeNameType,
          isSelected,
        } = item || {};

        if (isSelected) {
          selected.push(index);

          // Validate ALL selected travellers regardless of touch state
          // Check each required field and add error for the first missing one
          if (secondaryContactFields?.name?.isRequired && !fullName?.trim()) {
            errorArr.push({
              index,
              errorType: "name",
            });
          } else if (secondaryContactFields?.dob?.isRequired && !dobValue) {
            errorArr.push({
              index,
              errorType: "dob",
            });
          } else if (
            secondaryContactFields?.gender?.isRequired &&
            !genderType?.trim()
          ) {
            errorArr.push({
              index,
              errorType: "gender",
            });
          } else if (
            secondaryContactFields?.passport?.isRequired &&
            !passportType?.trim()
          ) {
            errorArr.push({
              index,
              errorType: "passport",
            });
          } else if (
            secondaryContactFields?.nomineeName?.isRequired &&
            !nomineeNameType?.trim()
          ) {
            errorArr.push({
              index,
              errorType: "nomineeName",
            });
          }
        }
      });
      await filterTravellers(selected, errorArr);

      // If there are errors, expand first traveller with error and skip handleSubmit
      if (errorArr.length > 0) {
        // After filterTravellers, error travellers are moved to the top (starting from index 0)
        setExpandedTravellerIndex(0); // Expand first traveller with error
        if (setFirstErrorIndex) {
          setFirstErrorIndex(0); // First error is now at index 0 after reordering
        }

        if (!isFieldError) {
          setIsFieldError(true);
          handleErrorToast();
        }

        // Scroll to error traveller
        scrollToErrorTraveller(0, 350);
        return; // Don't call handleSubmit if there are errors
      }

      const formSnapshot = getValues();
      methods.reset(
        {
          ...formSnapshot,
          travellerData: reorderTravellersBySelectionOrder(
            formSnapshot.travellerData || []
          ),
        },
        { keepDefaultValues: true }
      );
      await new Promise((resolve) => setTimeout(resolve, 50));

      handleSubmit(onSubmit, onSubmitError)();
    }
  };

  type Position = "top" | "left" | "right";
  const templateIdCounter: Record<Position, Record<string, number>> = {
    top: {},
    left: {},
    right: {},
  };

  return (
    <TravellerDetailLayoutV2 id="pageId">
      <HeadComp>
        <BreadcrumbComponent />
        {components?.map((card) => {
          const CardComponent = MYPARTNER_COMPONENT_MAPPER["top"][card.id];
          if (CardComponent) {
            templateIdCounter.top[card.id] =
              templateIdCounter.top[card.id] || 0;
            templateIdCounter.top[card.id]++;
            return (
              <CardComponent
                key={`top-${card.id}-${templateIdCounter.top[card.id]}`}
                {...card.data}
              />
            );
          }
          return null;
        })}
      </HeadComp>
      <MainContainer>
        <LeftComp>
          <FormProvider {...methods}>
            {components?.map((card) => {
              const CardComponent = MYPARTNER_COMPONENT_MAPPER["left"][card.id];
              if (CardComponent) {
                templateIdCounter.left[card.id] =
                  templateIdCounter.left[card.id] || 0;
                templateIdCounter.left[card.id]++;
                return (
                  <CardComponent
                    key={`left-${card.id}-${templateIdCounter.left[card.id]}`}
                    {...card.data}
                    handleTncCheckbox={handleTncCheckbox}
                    setValue={setValue}
                    formValues={formValues}
                    isLoading={isLoading}
                    travellerRef={travellerRef}
                    firstErrorIndex={firstErrorIndex}
                    selectedCount={selectedCount}
                    travellerCount={travellerCount}
                    setSecondaryContactFields={setSecondaryContactFields}
                    primaryContactRef={primaryContactRef}
                    tncRef={tncRef}
                    onPayment={handleBuy}
                    expandedTravellerIndex={expandedTravellerIndex}
                    setExpandedTravellerIndex={setExpandedTravellerIndex}
                    visibleTravellersCount={
                      pageData?.visibleTravellersCount || 5
                    }
                    onExcelFileUploaded={setUploadedExcelFile}
                    forceShowAllTravellers={forceShowAllTravellers}
                  />
                );
              }
              return null;
            })}
          </FormProvider>
        </LeftComp>
        <RightComp>
          {components?.map((card) => {
            const CardComponent = MYPARTNER_COMPONENT_MAPPER["right"][card.id];
            if (CardComponent) {
              templateIdCounter.right[card.id] =
                templateIdCounter.right[card.id] || 0;
              templateIdCounter.right[card.id]++;
              return (
                <CardComponent
                  key={`right-${card.id}-${templateIdCounter.right[card.id]}`}
                  {...card.data}
                  onPayment={handleBuy}
                  isLoading={isLoading}
                  formValues={formValues}
                  travellerRef={travellerRef}
                  firstErrorIndex={firstErrorIndex}
                  selectedCount={selectedCount}
                  travellerCount={travellerCount}
                  setSecondaryContactFields={setSecondaryContactFields}
                  primaryContactRef={primaryContactRef}
                  tncRef={tncRef}
                  expandedTravellerIndex={expandedTravellerIndex}
                  setExpandedTravellerIndex={setExpandedTravellerIndex}
                />
              );
            }
            return null;
          })}
        </RightComp>
      </MainContainer>

      {isSelectedError && (
        <WebToast
          toastTxt={`Already selected ${travellerCount} ${
            travellerCount === 1 ? "traveller" : "travellers"
          }, Remove before adding one.`}
          position="fixed"
          right={"50px"}
          margin={device === "lg" ? "0px 0px 0px 28px" : ""}
          height="40px"
          bottom={device === "sm" ? "70px" : "50px"}
        />
      )}

      {isFieldError && (
        <WebToastWrap>
          <WebToast
            type="error"
            position="fixed"
            right={"50px"}
            bottom={device === "sm" ? "70px" : "50px"}
            toastTxt={
              selectedCount === travellerCount
                ? pageFormErrorToast?.message || formErrorToast
                : pageAddErrorToast?.message || addErrorToast
            }
            color="#6B4300"
          />
        </WebToastWrap>
      )}
    </TravellerDetailLayoutV2>
  );
};

export default InsTravDetailV2;
